/*
 * (c) Copyright, 2023, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import _ from 'lodash'
import type { SingleValue, MultiValue } from 'react-select'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import { useServiceContext } from '~/root'
import { InfiniteScrollSelect } from '../InfiniteScrollSelect'
import { Select } from '../Select'

interface OptionType {
  value: number
  label: string
}

interface DocTypeDropdownProps {
  id?: string
  required?: boolean
  isSingle?: boolean
  disabled?: boolean
  allowedTypes?: number[]
  placeholder?: string
  name?: string
  fromDocMig?: boolean // Flag to indicate if the dropdown is used within Document Migration context, which may have specific allowed types logic
  onChange?: (option: OptionType | OptionType[] | null) => void
}

const DocTypeDropdown = (props: DocTypeDropdownProps) => {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const [allowedDocumentTypeTemplate, setAllowedDocumentTypeTemplate] = useState<OptionType | OptionType[] | null>(null)
  const [showAsync, setShowAsync] = useState(true)
  const [id, setId] = useState('')
  const [isRequired, setIsRequired] = useState(false)
  const [isSingle, setIsSingle] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [allowedTypes, setAllowedTypes] = useState<number[]>([])
  const [placeholder, setPlaceholder] = useState('SELECT_DOC_TYPES_PLACEHOLDER...')
  const [availableTypes, setAvailableTypes] = useState<OptionType[]>([])
  const { setFieldValue } = useFormikContext()

  useEffect(() => {
    setId(props?.id ?? _.uniqueId('doctypes_select_'))
    setIsRequired(!!props?.required)
    setIsSingle(!!props?.isSingle)
    setIsDisabled(!!props?.disabled)
    setAllowedTypes(props?.allowedTypes ?? [])
    setPlaceholder(props?.placeholder ?? 'SELECT_DOC_TYPES_PLACEHOLDER...')
  }, [])

  useEffect(() => {
    if (_.isArray(props?.allowedTypes) && props?.allowedTypes.length) {
      context.api.documentTypes.getAllForAsyncPicker('', props?.allowedTypes ?? []).then(function (
        data: OptionType[] = []
      ) {
        const availableTypesData = data ?? []
        setShowAsync(false)
        setAvailableTypes(availableTypesData)
        if (availableTypesData.length === 1 && _.isFunction(props?.onChange)) {
          if (props.fromDocMig) {
            console.log('wait to run react state batch update when fromDocMig is true')
            setTimeout(() => {
              props.onChange?.({ ...availableTypesData[0] })
            }, 0)
          } else {
            console.log('wait to run react state batch update')
            props.onChange?.({ ...availableTypesData[0] })
          }
        }
      })
    }
  }, [props?.allowedTypes])

  return (
    <>
      {showAsync && (
        <div>
          <div>
            {isSingle && (
              <div>
                <InfiniteScrollSelect
                  placeholder={translate(placeholder)}
                  name={props?.name}
                  value={allowedDocumentTypeTemplate}
                  asyncLoadFn={async (search: string, page: number) => {
                    const response = (await context.api.documentTypes.getOptionsForAsyncPicker(
                      search,
                      page
                    )) as unknown as { options?: OptionType[]; hasMore?: boolean }
                    return {
                      options: response?.options ?? [],
                      hasMore: response?.hasMore ?? false
                    }
                  }}
                  onChange={(option: OptionType | OptionType[] | null) => {
                    setAllowedDocumentTypeTemplate(option ?? null)
                    if (_.isFunction(props?.onChange)) {
                      props.onChange(option)
                    }
                  }}
                  searchable
                  closeMenuOnSelect={true}
                  isClearable={false}
                  id="b4a34900-933e-439c-a88b-a92880a90074"
                />
              </div>
            )}
            {!isSingle && (
              <div>
                <InfiniteScrollSelect
                  placeholder={translate(placeholder)}
                  name={props?.name}
                  value={allowedDocumentTypeTemplate}
                  asyncLoadFn={async (search: string, page: number) => {
                    const response = (await context.api.documentTypes.getOptionsForAsyncPicker(
                      search,
                      page
                    )) as unknown as { options?: OptionType[]; hasMore?: boolean }
                    return {
                      options: response?.options ?? [],
                      hasMore: response?.hasMore ?? false
                    }
                  }}
                  onChangeHandler={(option: OptionType | OptionType[] | null) => {
                    setAllowedDocumentTypeTemplate(option ?? null)
                    if (_.isFunction(props?.onChange)) {
                      props.onChange(option)
                    }
                  }}
                  isMulti
                  searchable
                  closeMenuOnSelect={true}
                  isClearable={false}
                  id="08cb04df-c00d-4e2b-b7fd-284f513fb7cd"
                />
              </div>
            )}
          </div>
        </div>
      )}
      {!showAsync && (
        <div>
          {availableTypes.length != 1 && (
            <div>
              <Select
                id={id}
                placeholder={translate(placeholder)}
                required={isRequired}
                options={availableTypes}
                isClearable={false}
                onChange={(option: SingleValue<OptionType> | MultiValue<OptionType>) => {
                  // MultiValue<OptionType> is readonly OptionType[], so cast to OptionType[]
                  const value = Array.isArray(option) ? ([...option] as OptionType[]) : (option as OptionType | null)
                  setAllowedDocumentTypeTemplate(value ?? null)
                  if (_.isFunction(props?.onChange)) {
                    props.onChange(value)
                  }
                }}
              />
            </div>
          )}
          {availableTypes.length === 1 && <p>{availableTypes[0]?.label ?? ''}</p>}
        </div>
      )}
    </>
  )
}

export default DocTypeDropdown
